import { useState, useEffect, useCallback, useRef } from 'react';
import { Editor } from '@tiptap/react';

export interface TocEntry {
  id: string;
  level: 1 | 2 | 3;
  text: string;
  pos: number;
}

export function useEditorTableOfContents(editor: Editor | null) {
  const [entries, setEntries] = useState<TocEntry[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const extractHeadings = useCallback(() => {
    if (!editor) return;
    const doc = editor.state.doc;
    const headings: TocEntry[] = [];
    let index = 0;

    doc.descendants((node, pos) => {
      if (node.type.name === 'heading') {
        const level = node.attrs.level as number;
        if (level >= 1 && level <= 3) {
          headings.push({
            id: `toc-heading-${index}`,
            level: level as 1 | 2 | 3,
            text: node.textContent,
            pos,
          });
          index++;
        }
      }
    });

    setEntries(headings);
  }, [editor]);

  // Listen to editor updates to re-extract headings
  useEffect(() => {
    if (!editor) return;
    extractHeadings();
    editor.on('update', extractHeadings);
    return () => {
      editor.off('update', extractHeadings);
    };
  }, [editor, extractHeadings]);

  // Assign data-toc-id attributes to heading DOM nodes
  useEffect(() => {
    if (!editor || entries.length === 0) return;
    const editorDom = editor.view.dom;
    const headingEls = editorDom.querySelectorAll('h1, h2, h3');
    headingEls.forEach((el, i) => {
      if (i < entries.length) {
        el.setAttribute('data-toc-id', entries[i].id);
      }
    });
  }, [editor, entries]);

  // IntersectionObserver to track the active heading
  useEffect(() => {
    if (!editor || entries.length === 0) return;
    const editorDom = editor.view.dom;
    const scrollContainer = editorDom.closest('.overflow-auto');

    observerRef.current?.disconnect();

    const visibleIds = new Set<string>();

    observerRef.current = new IntersectionObserver(
      (intersections) => {
        intersections.forEach((entry) => {
          const tocId = (entry.target as HTMLElement).dataset.tocId;
          if (!tocId) return;
          if (entry.isIntersecting) {
            visibleIds.add(tocId);
          } else {
            visibleIds.delete(tocId);
          }
        });

        // Active = first visible heading in document order
        const orderedIds = entries.map((e) => e.id);
        const firstVisible = orderedIds.find((id) => visibleIds.has(id));
        if (firstVisible) {
          setActiveId(firstVisible);
        }
      },
      {
        root: scrollContainer instanceof Element ? scrollContainer : null,
        rootMargin: '-10% 0px -80% 0px',
        threshold: 0,
      }
    );

    const headingEls = editorDom.querySelectorAll('[data-toc-id]');
    headingEls.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, [editor, entries]);

  const scrollToHeading = useCallback(
    (entry: TocEntry) => {
      if (!editor) return;

      editor.chain().focus().setTextSelection(entry.pos + 1).run();

      const editorDom = editor.view.dom;
      const el = editorDom.querySelector(`[data-toc-id="${entry.id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    [editor]
  );

  return { entries, activeId, scrollToHeading };
}
